import { useEffect, useRef } from "react";
import { OptionsHttpMethods } from "src/models/optionsValues";

interface OutputProps {
    httpRequest: (method: OptionsHttpMethods, addr: string, body?: any) => any;
    multipleHttpRequests: (requests: {method: OptionsHttpMethods, addr: string, body?: any}[]) => any;
    cancellableHttpRequest: (method: OptionsHttpMethods, addr: string, body?: any) => any;
}
const useApiService = (): OutputProps => {
    const token = ``;
    const controller = useRef<AbortController>(new AbortController());
    const cancellableController = useRef<AbortController>();

    useEffect(() => {
        return () => {
            if (controller.current && !controller.current.signal.aborted) {
                controller.current.abort();
            }
            if (cancellableController.current && !cancellableController.current.signal.aborted) {
                cancellableController.current.abort();
            }
        }
    }, [])

    const httpRequest = (method: OptionsHttpMethods, addr: string, body?: any) => {
        const requestOptions: RequestInit = {
            method: method,
            signal: controller.current.signal,
            ...(body) && { body: JSON.stringify(body) }
        }
        return fetchSecure(addr, requestOptions)
        .then((response) => handleResponse(response))
        .catch((error) => handleError(addr, error))
    }

    const multipleHttpRequests = (requests: {method: OptionsHttpMethods, addr: string, body?: any}[]) => {
        return Promise.all(requests.map(({ method, addr, body }) =>
            httpRequest(method, addr, body)));
    }

    const cancellableHttpRequest = (method: OptionsHttpMethods, addr: string, body?: any) => {
        if (cancellableController.current && cancellableController.current?.signal.aborted === false) {
            cancellableController.current.abort();
        } 
        cancellableController.current = new AbortController();

        const requestOptions: RequestInit = {
            method: method,
            signal: cancellableController.current.signal,
            ...(body) && { body: JSON.stringify(body) }
        }
        return fetchSecure(addr, requestOptions)
        .then((response) => handleResponse(response))
        .catch((error) => handleError(addr, error))
    }

    const handleResponse = async (response: Response): Promise<Response> => {
        if (response.status >= 400 && response.status <= 599) {
            const errorBody = await response.json();
            throw new Error(`${response.status}: ${errorBody.message}`)
        }
        return response.json();
    }

    const handleError = (addr: string, error: Error) => {
        if (error instanceof DOMException && error.name === "AbortError") {
            const abortError = new Error("Request aborted");
            // Attach custom flag
            (abortError as any).aborted = true;
            throw abortError;
        }
        throw error;
    }

    const fetchSecure = async (addr: string, requestOptions: RequestInit): Promise<Response> => {
        return fetch(addr, getHeaders(requestOptions));
    };
    
    const getHeaders = (requestOptions = {}): RequestInit => {
        const newRequestHeaders: RequestInit = { ...requestOptions};
        newRequestHeaders.credentials = 'include';
        newRequestHeaders.headers = {
            // Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
        return newRequestHeaders;
    }

    return {
        httpRequest,
        cancellableHttpRequest,
        multipleHttpRequests
    }
}
export default useApiService